import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Spinner from '../Spinner';
import {
  AddOnDto,
  AdminCreateUserRequest,
  AdminUpdateUserRequest,
  SubscriptionPricingDto,
  TierDto,
  UserAuditRecord,
  UserDetails,
  UserRole,
  UserSubscriptionDetailsDto,
} from '../../types/userManagement';

interface DialogProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
}

interface FormDialogProps {
  children: ReactNode;
  onSubmit: (event: FormEvent) => void;
  onClose: () => void;
  submitLabel: string;
  loading?: boolean;
  error?: string;
}

interface UserActionDialogProps {
  user: UserDetails | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  error?: string;
}

export function ManagementModal({ title, isOpen, onClose, children }: DialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <header className="flex items-center justify-between border-b border-strivre-lightnavy p-6">
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <button className="rounded-lg p-2 text-slate-600 hover:bg-strivre-softgrey" onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function ModalForm({ children, onSubmit, onClose, submitLabel, loading, error }: FormDialogProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 p-6 text-left">
      {children}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <footer className="flex gap-3 pt-3">
        <button type="button" className="btn-secondary btn-md flex-1" disabled={loading} onClick={onClose}>Cancel</button>
        <button type="submit" className="btn-primary btn-md flex flex-1 items-center justify-center gap-2" disabled={loading}>
          {loading && <Spinner size="h-4 w-4" />}
          {submitLabel}
        </button>
      </footer>
    </form>
  );
}

export function CreateUserModal({ isOpen, onClose, onSave, brandingId, tiers, addOns, loading, error }: { isOpen: boolean; onClose: () => void; onSave: (request: AdminCreateUserRequest) => void; brandingId: string; tiers: TierDto[]; addOns: AddOnDto[]; loading?: boolean; error?: string }) {
  const [form, setForm] = useState<AdminCreateUserRequest>({ email: '', firstName: '', lastName: '', brandingId, roles: ['User'], tierId: null, addOnIds: [] });

  useEffect(() => {
    if (isOpen) setForm({ email: '', firstName: '', lastName: '', brandingId, roles: ['User'], tierId: null, addOnIds: [] });
  }, [brandingId, isOpen]);

  function toggleAddOn(addOnId: number) {
    setForm((current) => ({ ...current, addOnIds: current.addOnIds?.includes(addOnId) ? current.addOnIds.filter((id) => id !== addOnId) : [...(current.addOnIds ?? []), addOnId] }));
  }

  return <ManagementModal title="Create user" isOpen={isOpen} onClose={onClose}><ModalForm onClose={onClose} loading={loading} error={error} submitLabel="Send invitation" onSubmit={(event) => { event.preventDefault(); onSave(form); }}><div className="grid gap-4 sm:grid-cols-2"><InputField label="First name" value={form.firstName ?? ''} onChange={(firstName) => setForm({ ...form, firstName })} /><InputField label="Last name" value={form.lastName ?? ''} onChange={(lastName) => setForm({ ...form, lastName })} /></div><InputField label="Email" type="email" value={form.email} onChange={(email) => setForm({ ...form, email })} /><label className="block text-sm font-semibold text-slate-700">Role<select className="input-base mt-1 w-full" value={form.roles?.[0] ?? 'User'} onChange={(event) => setForm({ ...form, roles: [event.target.value as UserRole] })}><option value="User">User</option><option value="Admin">Admin</option></select></label><label className="block text-sm font-semibold text-slate-700">Tier<select className="input-base mt-1 w-full" value={form.tierId ?? ''} onChange={(event) => setForm({ ...form, tierId: event.target.value ? Number(event.target.value) : null })}><option value="">No tier</option>{tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}</select></label><fieldset><legend className="text-sm font-semibold text-slate-700">Add-ons</legend><div className="mt-2 max-h-32 space-y-2 overflow-y-auto rounded-lg border border-strivre-lightnavy p-3">{addOns.length ? addOns.map((addOn) => <label key={addOn.id} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.addOnIds?.includes(addOn.id)} onChange={() => toggleAddOn(addOn.id)} />{addOn.name}</label>) : <p className="text-sm text-slate-400">No add-ons available.</p>}</div></fieldset></ModalForm></ManagementModal>;
}

function InputField({ label, type = 'text', value, onChange }: { label: string; type?: string; value: string; onChange: (value: string) => void }) { return <label className="block text-sm font-semibold text-slate-700">{label}<input required type={type} className="input-base mt-1 w-full" value={value} onChange={(event) => onChange(event.target.value)} /></label>; }

export function EditUserModal({ user, subscription, pricing, tiers, addOns, onClose, onSave, loading, error }: { user: UserDetails | null; subscription?: UserSubscriptionDetailsDto; pricing?: SubscriptionPricingDto; tiers: TierDto[]; addOns: AddOnDto[]; onClose: () => void; onSave: (request: AdminUpdateUserRequest) => void; loading?: boolean; error?: string }) {
  const [form, setForm] = useState<AdminUpdateUserRequest>({});

  useEffect(() => {
    setForm({
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      tierId: subscription?.tier.id ?? null,
      addOnIds: subscription?.purchasedAddOns.map((addOn) => addOn.id) ?? [],
    });
  }, [subscription, user]);

  function toggleAddOn(addOnId: number) {
    setForm((current) => ({
      ...current,
      addOnIds: current.addOnIds?.includes(addOnId)
        ? current.addOnIds.filter((id) => id !== addOnId)
        : [...(current.addOnIds ?? []), addOnId],
    }));
  }

  return <ManagementModal title="Edit user" isOpen={Boolean(user)} onClose={onClose}><ModalForm onClose={onClose} loading={loading} error={error} submitLabel="Save changes" onSubmit={(event) => { event.preventDefault(); onSave(form); }}><div className="grid gap-4 sm:grid-cols-2"><InputField label="First name" value={form.firstName ?? ''} onChange={(firstName) => setForm({ ...form, firstName })} /><InputField label="Last name" value={form.lastName ?? ''} onChange={(lastName) => setForm({ ...form, lastName })} /></div><div className="rounded-lg border border-strivre-lightnavy bg-slate-50 p-3 text-sm text-slate-600"><p><span className="font-semibold text-slate-800">Email:</span> {user?.email}</p><p className="mt-1"><span className="font-semibold text-slate-800">Roles:</span> {user?.roles.join(', ')}</p><p className="mt-1"><span className="font-semibold text-slate-800">Subscription status:</span> {subscription?.status ?? 'No active subscription'}</p></div><label className="block text-sm font-semibold text-slate-700">Tier<select className="input-base mt-1 w-full" value={form.tierId ?? ''} onChange={(event) => setForm({ ...form, tierId: event.target.value ? Number(event.target.value) : null })}><option value="">No tier</option>{tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}</select></label><fieldset><legend className="text-sm font-semibold text-slate-700">Add-ons</legend><div className="mt-2 max-h-32 space-y-2 overflow-y-auto rounded-lg border border-strivre-lightnavy p-3">{addOns.length ? addOns.map((addOn) => <label key={addOn.id} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.addOnIds?.includes(addOn.id)} onChange={() => toggleAddOn(addOn.id)} />{addOn.name}</label>) : <p className="text-sm text-slate-400">No add-ons available.</p>}</div></fieldset>{pricing && <div className="rounded-lg border border-strivre-lightnavy bg-slate-50 p-3 text-sm text-slate-600"><p className="font-semibold text-slate-800">Current Pricing</p><p className="mt-1">Tier: {formatPrice(pricing.tierMonthlyPrice)}/month | {formatPrice(pricing.tierAnnualPrice)}/year</p><p>Add-ons: {formatPrice(pricing.addOnsMonthlyTotal)}/month | {formatPrice(pricing.addOnsAnnualTotal)}/year</p><p className="font-semibold text-slate-800">Total: {formatPrice(pricing.grandMonthlyTotal)}/month | {formatPrice(pricing.grandAnnualTotal)}/year</p></div>}</ModalForm></ManagementModal>;
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-slate-200 py-2 last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>

      <div className="text-sm font-medium text-slate-800">
        {value}
      </div>
    </div>
  );
}

export function ViewUserModal({
  user,
  onClose,
  onAudit,
  subscription,
  pricing,
  subscriptionLoading,
  onRemoveAddOn,
  removingAddOnId,
}: {
  user: UserDetails | null;
  onClose: () => void;
  onAudit: () => void;
  subscription?: UserSubscriptionDetailsDto;
  pricing?: SubscriptionPricingDto;
  subscriptionLoading?: boolean;
  onRemoveAddOn: (addOnId: number) => void;
  removingAddOnId?: number;
}) {
  return (
    <ManagementModal
      title="User details"
      isOpen={Boolean(user)}
      onClose={onClose}
    >
      {user && (
        <div className="p-6">
            <div className="grid gap-4 lg:grid-cols-2">
            <DetailsSection title="User Information">
                <DetailItem label="Full Name" value={`${user.firstName} ${user.lastName}`} />
                <DetailItem label="Email Address" value={user.email} />
                <DetailItem label="Roles" value={user.roles.join(', ')} />
            </DetailsSection>

            <DetailsSection title="Account Information">
                <DetailItem
                label="Status"
                value={
                    <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {user.status}
                    </span>
                }
                />

                <DetailItem
                label="Created"
                value={new Date(user.createdDate).toLocaleString()}
                />

                <DetailItem
                label="Last Login"
                value={
                    user.lastLoginDate
                    ? new Date(user.lastLoginDate).toLocaleString()
                    : 'Never'
                }
                />
            </DetailsSection>

            <DetailsSection title="Security Information">
                <DetailItem
                label="Password Changed"
                value={
                    user.passwordLastChangedDate
                    ? new Date(user.passwordLastChangedDate).toLocaleString()
                    : 'Not available'
                }
                />

                <DetailItem
                label="Password Expires"
                value={
                    user.passwordExpirationDate
                    ? new Date(user.passwordExpirationDate).toLocaleString()
                    : 'Not set'
                }
                />

                <DetailItem
                label="Account Lock"
                value={user.isLocked ? 'Locked' : 'Not Locked'}
                />

                <DetailItem
                label="Failed Sign-ins"
                value={String(user.failedLoginAttempts)}
                />
            </DetailsSection>

            <DetailsSection title="Subscription Information">
              {subscriptionLoading ? (
                <div className="flex justify-center py-4"><Spinner size="h-5 w-5" /></div>
              ) : subscription ? (
                <>
                  <DetailItem label="Subscription Status" value={subscription.status} />
                  <DetailItem label="Start Date" value={new Date(subscription.startDate).toLocaleDateString()} />
                  <DetailItem label="End Date" value={subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'Not set'} />

                  <div className="mt-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tier Information</h4>
                    <DetailItem label="Tier Name" value={subscription.tier.name} />
                    <DetailItem label="Monthly Price" value={formatPrice(subscription.tier.monthlyPrice)} />
                    <DetailItem label="Annual Price" value={formatPrice(subscription.tier.annualPrice)} />
                  </div>

                  <AddOnList title="Included Add-ons" addOns={subscription.includedAddOns} />
                  <AddOnList title="Purchased Add-ons" addOns={subscription.purchasedAddOns} onRemove={onRemoveAddOn} removingAddOnId={removingAddOnId} />

                  {pricing && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pricing Summary</h4>
                      <DetailItem label="Tier Monthly Price" value={formatPrice(pricing.tierMonthlyPrice)} />
                      <DetailItem label="Tier Annual Price" value={formatPrice(pricing.tierAnnualPrice)} />
                      <DetailItem label="Add-ons Monthly Total" value={formatPrice(pricing.addOnsMonthlyTotal)} />
                      <DetailItem label="Add-ons Annual Total" value={formatPrice(pricing.addOnsAnnualTotal)} />
                      <DetailItem label="Grand Monthly Total" value={formatPrice(pricing.grandMonthlyTotal)} />
                      <DetailItem label="Grand Annual Total" value={formatPrice(pricing.grandAnnualTotal)} />
                    </div>
                  )}
                </>
              ) : (
                <p className="mt-2 text-sm text-slate-500">No active subscription.</p>
              )}
            </DetailsSection>
            </div>

            <div className="mt-6 border-t border-strivre-lightnavy pt-4">
            <button
                className="btn-secondary btn-md"
                onClick={onAudit}
            >
                View Audit History
            </button>
            </div>
        </div>
        )}
    </ManagementModal>
  );
}

function AddOnList({ title, addOns, onRemove, removingAddOnId }: { title: string; addOns: AddOnDto[]; onRemove?: (addOnId: number) => void; removingAddOnId?: number }) {
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      {addOns.length ? addOns.map((addOn) => (
        <div key={addOn.id} className="mt-2 rounded-lg border border-strivre-lightnavy bg-white p-3">
          <p className="font-medium text-slate-800">{addOn.name}</p>
          <p className="text-sm text-slate-500">Monthly: {formatPrice(addOn.monthlyPrice)} | Annual: {formatPrice(addOn.annualPrice)}</p>
          {onRemove && <button type="button" onClick={() => onRemove(addOn.id)} disabled={removingAddOnId === addOn.id} className="mt-2 text-sm font-medium text-strivre-burgundy hover:underline disabled:opacity-50">{removingAddOnId === addOn.id ? 'Removing...' : 'Remove'}</button>}
        </div>
      )) : <p className="mt-2 text-sm text-slate-500">None.</p>}
    </div>
  );
}

function formatPrice(value: number) {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function DetailsSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-strivre-lightnavy bg-slate-50 p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-strivre-burgundy">
        {title}
      </h3>

      <div className="mt-3 text-sm leading-7 text-slate-700">
        {children}
      </div>
    </section>
  );
}

function StatusUserModal({ title, user, status, onClose, onConfirm, loading, error, requiresReason = false }: { title: string; user: UserDetails | null; status: 'Active' | 'Inactive' | 'Suspended'; onClose: () => void; onConfirm: (reason?: string) => void; loading?: boolean; error?: string; requiresReason?: boolean }) { const [reason, setReason] = useState(''); useEffect(() => setReason(''), [user]); return <ManagementModal title={title} isOpen={Boolean(user)} onClose={onClose}><ModalForm onClose={onClose} loading={loading} error={error} submitLabel="Confirm" onSubmit={(event) => { event.preventDefault(); onConfirm(reason); }}><p className="text-sm text-slate-600">Update {user?.email} to {status.toLowerCase()} status.</p>{requiresReason && <label className="block text-sm font-semibold text-slate-700">Suspension reason<textarea required className="input-base mt-1 w-full" value={reason} onChange={(event) => setReason(event.target.value)} /></label>}</ModalForm></ManagementModal>; }
export const ActivateUserModal = (props: Omit<Parameters<typeof StatusUserModal>[0], 'title' | 'status'>) => <StatusUserModal {...props} title="Activate user" status="Active" />;
export const DeactivateUserModal = (props: Omit<Parameters<typeof StatusUserModal>[0], 'title' | 'status'>) => <StatusUserModal {...props} title="Deactivate user" status="Inactive" />;
export const SuspendUserModal = (props: Omit<Parameters<typeof StatusUserModal>[0], 'title' | 'status' | 'requiresReason'>) => <StatusUserModal {...props} title="Suspend user" status="Suspended" requiresReason />;

function UserActionModal({ title, label, user, onClose, onConfirm, loading, error }: UserActionDialogProps & { title: string; label: string }) { return <ManagementModal title={title} isOpen={Boolean(user)} onClose={onClose}><ModalForm onClose={onClose} loading={loading} error={error} submitLabel={label} onSubmit={(event) => { event.preventDefault(); onConfirm(); }}><p className="text-sm text-slate-600">Continue for {user?.email}?</p></ModalForm></ManagementModal>; }
export const UnlockUserModal = (props: UserActionDialogProps) => <UserActionModal {...props} title="Unlock user" label="Unlock" />;
export const ForcePasswordResetModal = (props: UserActionDialogProps) => <UserActionModal {...props} title="Force password reset" label="Send reset" />;
export const ResendInvitationModal = (props: UserActionDialogProps) => <UserActionModal {...props} title="Resend invitation" label="Resend invitation" />;

export function AssignTierModal({ user, subscription, tiers, onClose, onConfirm, loading, error }: { user: UserDetails | null; subscription?: UserSubscriptionDetailsDto; tiers: TierDto[]; onClose: () => void; onConfirm: (tierId: number) => void; loading?: boolean; error?: string }) {
  const [tierId, setTierId] = useState('');

  useEffect(() => {
    setTierId(subscription?.tier.id ? String(subscription.tier.id) : '');
  }, [subscription, user]);

  return (
    <ManagementModal title="Assign tier" isOpen={Boolean(user)} onClose={onClose}>
      <ModalForm onClose={onClose} loading={loading} error={error} submitLabel="Assign tier" onSubmit={(event) => { event.preventDefault(); onConfirm(Number(tierId)); }}>
        <label className="block text-sm font-semibold text-slate-700">
          Tier
          <select required className="input-base mt-1 w-full" value={tierId} onChange={(event) => setTierId(event.target.value)}>
            <option value="">Select a tier</option>
            {tiers.map((tier) => <option key={tier.id} value={tier.id}>{tier.name}</option>)}
          </select>
        </label>
      </ModalForm>
    </ManagementModal>
  );
}

export function AssignAddOnsModal({ user, subscription, addOns, onClose, onConfirm, loading, error }: { user: UserDetails | null; subscription?: UserSubscriptionDetailsDto; addOns: AddOnDto[]; onClose: () => void; onConfirm: (ids: number[]) => void; loading?: boolean; error?: string }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    setSelectedIds(subscription?.purchasedAddOns.map((addOn) => addOn.id) ?? []);
    setSearchTerm('');
  }, [subscription, user]);

  const visibleAddOns = addOns.filter((addOn) => addOn.name.toLowerCase().includes(searchTerm.toLowerCase()));
  function toggleAddOn(id: number) { setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]); }

  return <ManagementModal title="Assign add-ons" isOpen={Boolean(user)} onClose={onClose}><ModalForm onClose={onClose} loading={loading} error={error} submitLabel="Save add-ons" onSubmit={(event) => { event.preventDefault(); onConfirm(selectedIds); }}><input className="input-base w-full" placeholder="Search add-ons" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /><p className="text-sm text-slate-500">{selectedIds.length} add-on(s) selected</p><div className="max-h-52 space-y-2 overflow-y-auto rounded-lg border border-strivre-lightnavy p-3">{visibleAddOns.map((addOn) => <label key={addOn.id} className="flex gap-2 text-sm text-slate-700"><input type="checkbox" checked={selectedIds.includes(addOn.id)} onChange={() => toggleAddOn(addOn.id)} />{addOn.name}</label>)}</div></ModalForm></ManagementModal>;
}

export function AuditHistoryModal({ isOpen, records, loading, onClose }: { isOpen: boolean; records?: UserAuditRecord[]; loading: boolean; onClose: () => void }) { return <ManagementModal title="Audit history" isOpen={isOpen} onClose={onClose}><div className="p-6">{loading ? <Spinner size="h-6 w-6" /> : records?.length ? <div className="space-y-3">{records.map((record) => <div key={record.id} className="border-b border-slate-100 pb-3 text-sm"><p className="font-semibold text-slate-800">{record.action}</p><p className="text-slate-500">{new Date(record.timestamp).toLocaleString()} by {record.actorName}</p>{record.details && <p className="mt-1 text-slate-600">{record.details}</p>}</div>)}</div> : <p className="text-sm text-slate-400">No audit history found.</p>}</div></ManagementModal>; }
